import React from "react";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { TemplatePicker } from "@/components/project/TemplatePicker";
import type { OfferLineTemplate } from "@/hooks/use-offers";
import type { ProjectPhase } from "@/lib/offer-types";

export interface BudgetLineData {
  id: string;
  phase: ProjectPhase | string;
  description: string;
  quantity_hours: number;
  amount: number;
  estimated_cost: number;
  has_time: boolean;
  cost_type?: "internal" | "external";
  /** VIB-584: null betyr at PDF-parser ikke fant en mal-match — bruker bør verifisere fase */
  template_id?: string | null;
  /** Cached navn fra mal-match for UI-visning. */
  template_name?: string | null;
  /** VIB-1021: tilvalg/opsjon detektert av Vision. Vises read-only her. */
  is_optional?: boolean;
}

interface BudgetLineRowProps {
  line: BudgetLineData;
  templates: OfferLineTemplate[];
  aiParsedValues: Record<string, { amount: number }>;
  onUpdate: (line: BudgetLineData) => void;
  onDelete: () => void;
}

export function BudgetLineRow({
  line,
  templates,
  aiParsedValues,
  onUpdate,
  onDelete,
}: BudgetLineRowProps) {
  return (
    <tr className="border-b border-border/40">
      <td className="py-1.5 px-2">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Input
              value={line.description}
              className="h-8 flex-1"
              onChange={e => onUpdate({ ...line, description: e.target.value })}
            />
            {line.is_optional && (
              <span
                className="inline-flex items-center shrink-0 gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-300"
                title="Tilvalg/opsjon — markeres i egen rubrikk på timetildeling"
              >
                Tilvalg
              </span>
            )}
          </div>
          <TemplatePicker
            templates={templates}
            currentId={line.template_id ?? null}
            currentName={line.template_name ?? null}
            onSelect={(t) => {
              if (t) {
                onUpdate({
                  ...line,
                  template_id: t.id,
                  template_name: t.name,
                  phase: t.phase as ProjectPhase,
                  cost_type: t.cost_type === "external" ? "external" : "internal",
                });
              } else {
                onUpdate({
                  ...line,
                  template_id: null,
                  template_name: null,
                });
              }
            }}
          />
        </div>
      </td>
      <td className="py-1.5 px-2">
        <Input
          type="number"
          className="h-8 w-full text-right"
          value={line.quantity_hours || ""}
          onChange={e =>
            onUpdate({ ...line, quantity_hours: Number(e.target.value) })
          }
        />
      </td>
      <td className="py-1.5 px-2">
        <div className="flex items-center gap-1">
          <div className="flex-1">
            <Input
              type="number"
              className="h-8 w-full text-right"
              value={line.amount || ""}
              onChange={e => onUpdate({ ...line, amount: Number(e.target.value) })}
            />
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="text-muted-foreground hover:text-foreground">
                  <SrIcon name="sparkles" className="w-3.5 h-3.5"  />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="text-xs">
                AI foreslo: {formatNOK(aiParsedValues[line.id]?.amount ?? line.amount)}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {aiParsedValues[line.id] &&
            line.amount !== aiParsedValues[line.id].amount &&
            Math.abs(
              (line.amount - aiParsedValues[line.id].amount) /
                (aiParsedValues[line.id].amount || 1)
            ) > 0.2 && (
              <div className="flex items-center gap-1">
                <div className="text-xs text-amber-600 font-medium">Avviker</div>
                <button
                  type="button"
                  onClick={() =>
                    onUpdate({
                      ...line,
                      amount: aiParsedValues[line.id].amount,
                    })
                  }
                  className="text-muted-foreground hover:text-foreground"
                >
                  <SrIcon name="rotate-ccw" className="w-3.5 h-3.5"  />
                </button>
              </div>
            )}
        </div>
      </td>
      <td className="py-1.5 px-2">
        <Input
          type="number"
          className="h-8 w-full text-right"
          value={line.estimated_cost || ""}
          placeholder="0"
          onChange={e =>
            onUpdate({ ...line, estimated_cost: Number(e.target.value) })
          }
        />
      </td>
      <td className="py-1.5 px-2">
        <button
          onClick={onDelete}
          className="text-muted-foreground hover:text-destructive"
        >
          <SrIcon name="trash2" className="w-3.5 h-3.5"  />
        </button>
      </td>
    </tr>
  );
}
